import { createInstanceFromWizard, loadTrip } from '../store.js';
import { ensureInstanceItinerary } from '../instance-itinerary.js';
import { bindAvatarEmojiPicker, readAvatarEmojiFromForm, renderAvatarEmojiPicker } from '../utils/avatar-emoji-picker.js';
import { escapeHtml } from '../utils/dom.js';
import { formatDateIT } from '../utils/format.js';
import { INSTANCE_WIZARD_STEPS, renderWizardShell } from './wizard-shared.js';

let state = null;

function step1Html(trip, plan) {
  return `
    <div class="wizard-form">
      <h2 class="view-title">${escapeHtml(trip.title)}</h2>
      <p class="wizard-hint">${escapeHtml(trip.country)} · ${formatDateIT(trip.date_start)} – ${formatDateIT(trip.date_end)}</p>
      <p class="wizard-lead">Stai per avviare il <strong>viaggio vivo</strong> per il tuo gruppo: checklist, spese, diario e note condivise partono da qui.</p>
      <div class="wizard-form__actions">
        <button type="button" class="btn-primary" data-wizard-next>Inizia setup →</button>
      </div>
    </div>
  `;
}

function step2Html() {
  return `
    <form class="wizard-form" id="wizard-form">
      <h2 class="view-title">Chi viaggia?</h2>
      <p class="wizard-hint">Il tuo nome compare nelle note e nelle spese condivise.</p>
      <label class="field">
        <span class="field-label">Nome visualizzato</span>
        <input class="field-input" name="displayName" required value="${escapeHtml(state.displayName)}" placeholder="es. Eric" />
      </label>
      <div class="field">
        <span class="field-label">Emoji avatar</span>
        ${renderAvatarEmojiPicker({ name: 'avatarEmoji', selected: state.avatarEmoji })}
      </div>
      <div class="wizard-form__actions">
        <button type="button" class="btn-ghost" data-wizard-back>← Indietro</button>
        <button type="submit" class="btn-primary">Continua →</button>
      </div>
    </form>
  `;
}

function step3Html() {
  return `
    <form class="wizard-form" id="wizard-form">
      <h2 class="view-title">Alloggi (opzionale)</h2>
      <p class="wizard-hint">Puoi aggiungerne altri dopo dal tab Personalizza.</p>
      <label class="field">
        <span class="field-label">Nome alloggio</span>
        <input class="field-input" name="baseName" value="${escapeHtml(state.baseName)}" placeholder="es. Appartamento Bled" />
      </label>
      <label class="field">
        <span class="field-label">Sottotitolo (opz.)</span>
        <input class="field-input" name="baseSubtitle" value="${escapeHtml(state.baseSubtitle)}" />
      </label>
      <div class="custom-form__row">
        <label class="field">
          <span class="field-label">Dal</span>
          <input class="field-input" name="baseStart" type="date" value="${escapeHtml(state.baseStart)}" />
        </label>
        <label class="field">
          <span class="field-label">Al</span>
          <input class="field-input" name="baseEnd" type="date" value="${escapeHtml(state.baseEnd)}" />
        </label>
      </div>
      <label class="field">
        <span class="field-label">Link Maps (opz.)</span>
        <input class="field-input" name="baseMapsUrl" type="url" value="${escapeHtml(state.baseMapsUrl)}" />
      </label>
      <div class="wizard-form__actions">
        <button type="button" class="btn-ghost" data-wizard-back>← Indietro</button>
        <button type="submit" class="btn-primary">Continua →</button>
        <button type="button" class="btn-ghost" id="wizard-skip-base">Salta</button>
      </div>
    </form>
  `;
}

function step4Html() {
  return `
    <div class="wizard-form">
      <h2 class="view-title">Tutto pronto!</h2>
      <p class="wizard-lead">Apri il viaggio e inizia a usare checklist, spese e diario con il tuo gruppo.</p>
      <div class="wizard-form__actions">
        <button type="button" class="btn-ghost" data-wizard-back>← Indietro</button>
        <button type="button" class="btn-primary" id="wizard-start">Apri viaggio</button>
      </div>
      <p class="wizard-hint status-error" id="wizard-error" hidden></p>
    </div>
  `;
}

export async function renderWizardInstance(container, tripId, step = 1) {
  if (!state || state.tripId !== tripId) {
    const tripData = await loadTrip(tripId);
    if (tripData.plan.status === 'draft') {
      location.hash = `#/new/plan/${tripId}`;
      return;
    }
    state = {
      tripId,
      planId: tripData.plan.id,
      trip: tripData.trip,
      plan: tripData.plan,
      planDays: tripData.days,
      displayName: 'Viaggiatore',
      avatarEmoji: '👤',
      baseName: '',
      baseSubtitle: '',
      baseStart: tripData.trip.date_start,
      baseEnd: tripData.trip.date_end,
      baseMapsUrl: '',
      step: 1,
    };
  }
  if (step) state.step = step;

  const body =
    state.step === 1
      ? step1Html(state.trip, state.plan)
      : state.step === 2
        ? step2Html()
        : state.step === 3
          ? step3Html()
          : step4Html();

  container.innerHTML = renderWizardShell({
    title: 'Avvia viaggio',
    subtitle: state.trip.title,
    steps: INSTANCE_WIZARD_STEPS,
    currentStep: state.step,
    backHref: '#/shelf',
    bodyHtml: body,
  });

  if (state.step === 2) {
    bindAvatarEmojiPicker(container);
  }

  const rerender = (s) => renderWizardInstance(container, tripId, s ?? state.step);

  container.querySelector('[data-wizard-next]')?.addEventListener('click', () => {
    state.step = 2;
    rerender(2);
  });

  container.querySelector('[data-wizard-back]')?.addEventListener('click', () => {
    state.step -= 1;
    rerender();
  });

  container.querySelector('#wizard-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    if (state.step === 2) {
      state.displayName = String(fd.get('displayName')).trim() || 'Viaggiatore';
      state.avatarEmoji = readAvatarEmojiFromForm(e.target);
      state.step = 3;
      rerender(3);
      return;
    }
    if (state.step === 3) {
      state.baseName = String(fd.get('baseName')).trim();
      state.baseSubtitle = String(fd.get('baseSubtitle')).trim();
      state.baseStart = String(fd.get('baseStart'));
      state.baseEnd = String(fd.get('baseEnd'));
      state.baseMapsUrl = String(fd.get('baseMapsUrl')).trim();
      state.step = 4;
      rerender(4);
    }
  });

  container.querySelector('#wizard-skip-base')?.addEventListener('click', () => {
    state.baseName = '';
    state.step = 4;
    rerender(4);
  });

  container.querySelector('#wizard-start')?.addEventListener('click', async () => {
    const errEl = container.querySelector('#wizard-error');
    const btn = container.querySelector('#wizard-start');
    btn.disabled = true;
    errEl.hidden = true;

    try {
      const bases = [];
      if (state.baseName && state.baseStart && state.baseEnd) {
        bases.push({
          name: state.baseName,
          subtitle: state.baseSubtitle,
          dateStart: state.baseStart,
          dateEnd: state.baseEnd,
          mapsUrl: state.baseMapsUrl,
        });
      }

      const instance = await createInstanceFromWizard({
        planId: state.planId,
        displayName: state.displayName,
        avatarEmoji: state.avatarEmoji,
        bases,
      });

      await ensureInstanceItinerary(instance.id, state.planDays);
      state = null;
      location.hash = `#/trip/${tripId}/days`;
    } catch (err) {
      errEl.textContent = err.message;
      errEl.hidden = false;
      btn.disabled = false;
    }
  });
}

export function clearWizardInstanceState() {
  state = null;
}
